import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { NumberField } from "@/components/generic/NumberField";
import { Slider } from "@/components/generic/Slider";
import { FilterGroupComponentProps } from "@/data/filters/filter-types";
import { useReportDirty } from "@/hooks/use-report-dirty";
import { createRangeFilter, rangeFilterToForm } from "@/utils/filter-utils";
import { rangeSchema } from "@/utils/schemas";
import { isOneOf } from "@/utils/type-guards";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { match } from "ts-pattern";
import { z } from "zod";

const schema = z.object({
  range: rangeSchema,
  growth: z.number().nullable(),
});

// Standard slider range constants
const SLIDER_MIN = -100;
const SLIDER_MAX = 100;

// Define standard step arrays for different ranges
const STEPS_MINUS_100_PLUS_500 = [
  -100, -90, -80, -70, -60, -50, -40, -30, -20, -15, -10, -5, 0, 5, 10, 20, 30,
  40, 50, 60, 75, 100, 150, 250, 500,
];

const STEPS_MINUS_500_PLUS_500 = [
  -500, -250, -150, -100, -75, -50, -30, -25, -20, -15, -10, -5, 0, 5, 10, 20,
  30, 40, 50, 60, 75, 100, 150, 250, 500,
];

export function EconomyFilter({
  group: {
    type = "number",
    title,
    description,
    hasGrowth = false,
    growthTitle,
    growthDescription,
    minValue = undefined,
    minRange = -100,
    maxRange = 100,
  },
  formId,
  queryFilters,
  update,
  setIsDirty,
}: FilterGroupComponentProps<
  "value" | "change",
  {
    type: "number" | "percentage";
    hasGrowth?: boolean;
    growthTitle?: string;
    growthDescription?: string;
    minValue?: number;
    minRange?: number;
    maxRange?: number;
  }
>) {
  let valueFilter = queryFilters.value;
  if (
    isOneOf(valueFilter?.operator, "EventFilter", "DepartmentSize") ||
    !valueFilter?.values
  )
    valueFilter = undefined;

  const changeFilter = hasGrowth ? queryFilters.change : undefined;

  // Determine which steps array to use based on range
  const getStepsArray = () => {
    if (!hasGrowth) return [];
    if (minRange === -100 && maxRange === 500) {
      return STEPS_MINUS_100_PLUS_500;
    } else if (minRange === -500 && maxRange === 500) {
      return STEPS_MINUS_500_PLUS_500;
    } else {
      // For custom ranges, generate reasonable steps
      const steps = [];

      // Add negative steps if needed
      if (minRange < 0) {
        let step = minRange;
        while (step < 0) {
          steps.push(Math.round(step));
          step = Math.min(-5, step / 1.5);
          if (Math.abs(step) < 5) break;
        }
      }

      // Add zero
      steps.push(0);

      // Add positive steps
      if (maxRange > 0) {
        let step = 5;
        while (step <= maxRange) {
          steps.push(step);
          step = Math.min(maxRange, step * 1.5);
          if (step >= maxRange) break;
        }
      }

      // Sort steps in ascending order
      return steps.sort((a, b) => a - b);
    }
  };

  const steps = getStepsArray();

  // Convert actual value to slider position (-100 to 100)
  const valueToSliderPosition = (value: number | null): number => {
    if (value === null || value === 0) return 0;

    // Find closest step in our steps array
    const closestStep = findClosestStep(value);
    const stepIndex = steps.indexOf(closestStep);
    const zeroIndex = steps.indexOf(0);

    // Convert to slider position
    if (closestStep < 0) {
      // For negative values, calculate position proportionally
      // Map from steps[0] (min value) to steps[zeroIndex-1] (closest to zero negative)
      // to slider values from SLIDER_MIN to just above 0
      return SLIDER_MIN + (stepIndex / (zeroIndex - 1)) * (0 - SLIDER_MIN);
    } else {
      // For positive values, calculate position proportionally
      // Map from steps[zeroIndex+1] (smallest positive) to steps[steps.length-1] (max)
      // to slider values from just above 0 to SLIDER_MAX
      return (
        ((stepIndex - zeroIndex) / (steps.length - 1 - zeroIndex)) * SLIDER_MAX
      );
    }
  };

  // Convert slider position (-100 to 100) to actual value
  const sliderPositionToValue = (position: number): number => {
    if (position === 0) return 0;

    let stepIndex;
    const zeroIndex = steps.indexOf(0);

    if (position < 0) {
      // For negative slider positions, map to the negative portion of our steps array
      const negativeRange = zeroIndex;
      const normalizedPos = (position - SLIDER_MIN) / (0 - SLIDER_MIN);
      stepIndex = Math.round(normalizedPos * (negativeRange - 1));
    } else {
      // For positive slider positions, map to the positive portion of our steps array
      const positiveRange = steps.length - 1 - zeroIndex;
      // Ensure we never get index zeroIndex (which is 0)
      // For small positive positions, we want the next index after zero
      stepIndex = Math.max(
        zeroIndex + 1,
        Math.round(zeroIndex + (position / SLIDER_MAX) * positiveRange),
      );
    }

    // Ensure index is within bounds
    stepIndex = Math.max(0, Math.min(steps.length - 1, stepIndex));

    // For debugging
    console.log(
      `Position: ${position}, Index: ${stepIndex}, Value: ${steps[stepIndex]}`,
    );

    return steps[stepIndex];
  };

  // Find the closest step value in our steps array
  const findClosestStep = (value: number): number => {
    // Ensure we don't go beyond defined range
    if (value <= minRange) return minRange;
    if (value >= maxRange) return maxRange;

    let closestStep = 0;
    let minDiff = Number.MAX_VALUE;

    for (const step of steps) {
      const diff = Math.abs(step - value);
      if (diff < minDiff) {
        minDiff = diff;
        closestStep = step;
      }
    }

    return closestStep;
  };

  // Map the initial growth value to slider scale
  const initialGrowth = changeFilter?.values?.[0]
    ? valueToSliderPosition(Number(changeFilter.values[0]))
    : null;

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      range: rangeFilterToForm(valueFilter),
      growth: initialGrowth,
    },
  });

  useReportDirty(formState.isDirty, setIsDirty);

  const submit = handleSubmit((data) => {
    const rangeFilter = createRangeFilter(data.range);
    const actualGrowthValue =
      data.growth !== null ? sliderPositionToValue(data.growth) : null;

    // Treat 0 as null (no value)
    const growthFilter = hasGrowth
      ? match(actualGrowthValue)
          .with(null, () => undefined)
          .with(0, () => undefined) // Explicitly handle 0 as undefined
          .when(
            (growth) => growth < 0,
            (growth) => ({
              operator: "LessThan" as const,
              values: [growth.toString()],
            }),
          )
          .when(
            (growth) => growth > 0,
            (growth) => ({
              operator: "GreaterThan" as const,
              values: [growth.toString()],
            }),
          )
          .otherwise((growth) => ({
            operator: "Equal" as const,
            values: [growth.toString()],
          }))
      : undefined;

    update({
      value: rangeFilter,
      change: growthFilter,
    });
  });

  return (
    <Form
      className="mx-auto flex h-full w-min flex-col items-center justify-between"
      id={formId}
      onSubmit={submit}
    >
      <div className="flex w-xl flex-col items-center gap-y-9">
        <SegmentHeader title={title} description={description} />
        <div className="flex w-full justify-between gap-x-4">
          <NumberField
            className="w-1/2"
            name="range.min"
            aria-label="Større end"
            placeholder="Større end"
            control={control}
            minValue={minValue}
            suffix={
              type === "number" ? (
                <span className="text-sm font-medium">DKK</span>
              ) : (
                <span className="text-lg leading-none font-medium">%</span>
              )
            }
          />
          <NumberField
            className="w-1/2"
            aria-label="Mindre end"
            placeholder="Mindre end"
            name="range.max"
            control={control}
            minValue={minValue}
            suffix={
              type === "number" ? (
                <span className="text-sm font-medium">DKK</span>
              ) : (
                <span className="text-lg leading-none font-medium">%</span>
              )
            }
          />
        </div>
      </div>

      {hasGrowth && (
        <div className="mt-12 flex w-full flex-col items-center gap-y-9">
          <SegmentHeader
            title={growthTitle || "Udvikling"}
            description={growthDescription || "Vækst eller fald over tid"}
          />
          <Controller
            control={control}
            name="growth"
            render={({ field }) => (
              <div className="relative w-full">
                <Slider<number>
                  aria-label={growthTitle || "Udvikling"}
                  minValue={SLIDER_MIN}
                  value={field.value ?? 0}
                  onChange={field.onChange}
                  maxValue={SLIDER_MAX}
                  step={1}
                  sliderOutput={({ state }) => {
                    const value = state.getThumbValue(0);
                    const actualValue = sliderPositionToValue(value);

                    // Don't show any output for 0/center position
                    if (value === 0 || actualValue === 0) return null;

                    const isNegative = actualValue < 0;
                    return `${
                      isNegative ? "Faldet" : "Steget"
                    } med mere end ${Math.abs(actualValue)}%`;
                  }}
                />
              </div>
            )}
          />
        </div>
      )}
    </Form>
  );
}
